import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import {
  CLIENT_TIMEOUT_CODE,
  FrontendErrorReporterService,
  NETWORK_ERROR_CODE,
} from '../../errors/frontend-error-reporter.service';
import { APP_ROUTE_HEADER } from './app-location.interceptor';

/**
 * Báo lỗi HTTP mà MÁY CHỦ KHÔNG tự ghi lên màn Giám sát → Lỗi hệ thống.
 *
 * Quy tắc: máy chủ đã lưu mọi phản hồi lỗi có 'errorId' (kể cả 4xx). Thứ còn thiếu là các lỗi
 * không đi qua middleware của máy chủ — 404/405 tuyến chết, 403 thiếu quyền, 502/503/504 của
 * cổng, status 0 mất mạng. Nên FE báo khi: status khác 401 (phiên hết hạn là luồng bình
 * thường), thân KHÔNG có errorId (có tức là máy chủ đã ghi — báo lại là bản trùng), và url
 * không phải chính endpoint báo cáo. Chặn bão: cùng (status, đường dẫn) tối đa một lần / 30s.
 *
 * Trước đây chỉ báo 'status >= 500' — nên 404/403/0 không bao giờ hiện lên màn Giám sát.
 */
export const frontendErrorReportingInterceptor: HttpInterceptorFn = (req, next) => {
  const reporter = inject(FrontendErrorReporterService);
  const startedAt = Date.now();
  // E-176 — route LÚC GỬI (appLocationInterceptor đứng trước đã gắn header); lỗi hết thời gian tới
  // sau 30 s, lúc người dùng có thể đã sang màn khác.
  const pageRoute = req.headers.get(APP_ROUTE_HEADER) ?? undefined;

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (shouldReportHttpError(error, req.url, reporter)) {
        reporter.report({
          url: req.url,
          statusCode: typeof error?.status === 'number' ? error.status : 0,
          errorId: null,
          message:
            error?.error?.message ??
            error?.error?.Message ??
            error?.message ??
            error?.statusText ??
            '',
          source: 'http',
          // E-115 — ảnh chụp để màn "Yêu cầu HTTP" chẩn đoán được. Interceptor này đứng SAU
          // errorHandlerInterceptor trong chuỗi nên `error.error` là thân THÔ máy chủ trả về.
          httpMethod: req.method,
          requestBody: req.body,
          // Thân rỗng (minimal API không đọc được JSON) ⇒ Angular cho `null`; gửi chuỗi rỗng để
          // màn phân biệt "phản hồi rỗng" với "không có phản hồi". Status 0 (mất mạng) không có
          // phản hồi nào — `error.error` lúc đó là ProgressEvent, không phải thân.
          responseBody: error?.status ? (error.error ?? '') : undefined,
          durationMs: Date.now() - startedAt,
          errorCode: httpErrorCode(error),
          pageRoute,
        });
      }

      return throwError(() => error);
    }),
  );
};

/** Tách riêng để spec đọc được từng vế của quy tắc. */
export function shouldReportHttpError(
  error: Partial<HttpErrorResponse> | null | undefined,
  url: string,
  reporter: Pick<FrontendErrorReporterService, 'isReportUrl'>,
): boolean {
  if (reporter.isReportUrl(url)) return false;
  const status = typeof error?.status === 'number' ? error.status : 0;
  if (status === 401) return false;
  const body = error?.error;
  const errorId =
    body && typeof body === 'object' ? (body.errorId ?? body.ErrorId ?? null) : null;
  return !(typeof errorId === 'string' ? errorId.trim() : errorId);
}

/**
 * E-176 — mã lỗi cho báo cáo, để dòng trên màn Lỗi hệ thống luôn có thứ tra được.
 *
 * 'timeoutInterceptor' (đứng SAU trong chuỗi) ném rxjs 'TimeoutError' — không phải
 * HttpErrorResponse, không có 'status' ⇒ trước đây dòng ghi không mã HTTP lẫn mã lỗi. Status 0 là
 * không tới được máy chủ. Có phản hồi thì lấy mã ERR_… đầu tiên trong thân, nếu có.
 */
export function httpErrorCode(error: IReportedHttpError | null | undefined): string | undefined {
  if (error?.name === 'TimeoutError') return CLIENT_TIMEOUT_CODE;
  const status = typeof error?.status === 'number' ? error.status : 0;
  if (status === 0) return NETWORK_ERROR_CODE;
  const body = asRecord(error?.error);
  const errors = body ? (body['errors'] ?? body['Errors']) : undefined;
  if (!Array.isArray(errors)) return undefined;
  for (const item of errors) {
    const e = asRecord(item);
    const code = e ? (e['errorCode'] ?? e['ErrorCode']) : undefined;
    if (typeof code === 'string' && code.trim()) return code.trim();
  }
  return undefined;
}

/**
 * Hình dạng lỗi chảy qua catchError: HttpErrorResponse, hoặc rxjs TimeoutError (chỉ có 'name' và
 * 'message') — nên mọi trường đều tuỳ chọn.
 */
export interface IReportedHttpError {
  name?: string;
  status?: number;
  error?: unknown;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
}
